import { defineStore } from 'pinia';
import { useLoading } from '@sa/hooks';
import qs from 'qs';
import { SetupStoreId } from '@/enum';
import {
  fectchGetAccountPull,
  fetchPostAccountAdd,
  fetchPostAccountAddV2,
  fetchPostAccountPermission
} from '@/service/api/system-manage';
import { fetchAddBaseInfo, fetchListCategory, fetchUpdateBaseForm } from '@/service/api/system-category';
import { fetchPostCustomersAdd } from '@/service/api/customers-manage';
import { fetchAddProduct, fetchChangeReleaseStatus, fetchUpdBaseProduct } from '@/service/api/product';
import { fetchAddMock } from '@/service/api/device-mock';

import { fetchPostOtaTaskCreate } from '@/service/api/product-firmware';

import { fetchPostUpdateAppliction, fetchSetReleaseStatus } from '@/service/api/system-application-template';

import { filterEmptyProps } from '@/utils/common';
import { $t } from '@/locales';

import { removeAll, setAll, setConfirmAll, setOnlyClose, setOnlyFoot } from './shared';

export const useFootStore = defineStore(SetupStoreId.LayoutFoot, () => {
  const { loading: footLoading, startLoading, endLoading } = useLoading();

  /** 新增账号 */
  async function accountAdd(params: Api.SystemManage.AddAccount) {
    startLoading();
    const { data, error } = await fetchPostAccountAdd(filterEmptyProps(params));

    endLoading();
    if (!error) {
      return { data, error };
    }
  }

  async function accountAddV2(params: Api.SystemManage.AddAccount) {
    startLoading();
    const { data, error } = await fetchPostAccountAddV2(filterEmptyProps(params));

    endLoading();
    return { data, error };
  }

  async function accountPull(userId: string) {
    startLoading();
    const { data, error } = await fectchGetAccountPull(userId);
    endLoading();
    return { data, error };
  }

  /** 权限设置 */
  async function setAccountPermission(params: any) {
    startLoading();
    const { data, error } = await fetchPostAccountPermission(params);

    endLoading();
    if (!error) {
      return true;
    }
  }

  /**
   * 新增-标准品类基础信息
   *
   * @param categoryName
   * @param classId
   * @param iconFile
   * @param description
   */
  async function addBaseInfo(params: Api.Category.FormModel) {
    startLoading();

    const formData = new FormData();
    formData.append('categoryName', params.categoryName);
    formData.append('classId', params.classId);
    formData.append('description', params.description);
    formData.append('iconFile', params.iconFile[0].file);

    const { data: categoryId, error } = await fetchAddBaseInfo(formData);
    endLoading();
    if (!error) {
      return categoryId;
    }
  }

  /**
   * 上架
   *
   * @param categoryId
   */
  async function listCategory(params: any) {
    startLoading();
    const { data: stateRes, error } = await fetchListCategory(qs.stringify(params));
    endLoading();
    if (!error) {
      return stateRes;
    }
  }

  /**
   * 更新-标准品类基础信息
   *
   * @param categoryName
   * @param classId
   * @param iconFile
   * @param description
   */
  async function updateBaseForm(data: any) {
    startLoading();
    const formData = new FormData();
    formData.append('categoryId', data.categoryId);
    formData.append('categoryName', data.categoryName);
    formData.append('classId', data.classId);
    formData.append('description', data.description);

    if (data.iconFile) {
      formData.append('iconFile', data.iconFile[0].file);
    }

    const { data: status, error } = await fetchUpdateBaseForm(formData);
    endLoading();
    if (!error && status === 1) {
      window.$message?.success?.($t('page.category.editBaseSuccess'));

      return true;
    }
  }

  /** 新增客户 */
  async function addCustomer(params: any) {
    startLoading();
    const { data, error } = await fetchPostCustomersAdd(params);
    endLoading();
    if (!error) {
      return true;
    }
  }

  /**
   * 新增-产品
   *
   * @param categoryId
   * @param nodeType
   * @param iconFile
   * @param productName
   * @param description
   */
  async function addBaseProduct(params: any) {
    startLoading();
    const formData = new FormData();
    formData.append('categoryId', params.categoryId);
    formData.append('firmwareType', params.firmwareType.join(','));
    formData.append('protocolIds', params.protocolIds.join(','));
    formData.append('nodeType', params.nodeType);
    formData.append('productName', params.productName);
    if (params.description) {
      formData.append('description', params.description);
    }
    formData.append('iconFile', params.iconFile[0].file);
    const { data: productId, error } = await fetchAddProduct(formData);
    endLoading();
    if (!error) {
      return productId;
    }
  }

  /**
   * 产品发布、下架
   *
   * @param productId
   * @param releaseStatus
   */
  async function changeReleaseStatus(param: any) {
    startLoading();

    const { data: status, error } = await fetchChangeReleaseStatus(qs.stringify(param));
    endLoading();
    if (!error && status === 1) {
      return true;
    }
  }

  /**
   * 更新-产品
   *
   * @param productId
   * @param categoryId
   * @param nodeType
   * @param iconFile
   * @param productName
   * @param description
   */
  async function updateBaseProduct(data: any) {
    startLoading();
    const formData = new FormData();
    formData.append('categoryId', data.categoryId);
    formData.append('nodeType', data.nodeType);
    formData.append('productId', data.productId);
    formData.append('productName', data.productName);
    formData.append('protocolIds', data.protocolIds);
    formData.append('firmwareType', data.firmwareType);
    if (data.description) {
      formData.append('description', data.description);
    }

    if (data.iconFile) {
      formData.append('iconFile', data.iconFile[0].file);
    }

    const { data: status, error } = await fetchUpdBaseProduct(formData);
    endLoading();
    if (!error) {
      window.$message?.success?.($t('page.product.editBaseSuccess'));
      return true;
    }
  }

  /** 创建OTA任务 */
  async function createOtaTask(param: any) {
    startLoading();

    const { data, error } = await fetchPostOtaTaskCreate(param);
    endLoading();
    if (!error) {
      return true;
    }
  }

  /** 新增-Mock设备 */
  async function addMock(params: any) {
    startLoading();
    const { data, error } = await fetchAddMock(params);

    endLoading();
    if (!error) {
      window.$message?.success($t('page.mock.addMockSuccess'));
      return true;
    }
  }

  /** 更新应用模板 */
  async function updateBaseApplication(data: any) {
    startLoading();
    const formData = new FormData();
    formData.append('applicationTemplateName', data.applicationTemplateName);
    formData.append('applicationType', data.applicationType);
    formData.append('multiLogin', data.multiLogin);
    formData.append('mixedDevelop', data.mixedDevelop);
    formData.append('remark', data.remark);
    formData.append('id', data.id);
    formData.append(
      'policyTypes',
      Array.isArray(data.policyTypes) ? data.policyTypes.sort().join(',') : data.policyTypes
    );
    formData.append('languages', Array.isArray(data.languages) ? data.languages.join(',') : data.languages);
    if (data.iconFile) {
      formData.append('templateFile', data.iconFile[0].file);
    }
    if (data.icon instanceof File) {
      formData.append('templateFile', data.icon);
    }
    const { data: status, error } = await fetchPostUpdateAppliction(formData);
    endLoading();
    if (!error) {
      return true;
    }
  }

  /**
   * 产品发布、下架
   *
   * @param productId
   * @param releaseStatus
   */
  async function changeApplicationStatus(param: any) {
    const { data: status, error } = await fetchSetReleaseStatus(param);
    endLoading();
    if (!error) {
      return true;
    }
  }

  return {
    footLoading,
    accountAdd,
    accountAddV2,
    accountPull,
    setAccountPermission,
    setConfirmAll,
    removeAll,
    setAll,
    setOnlyClose,
    addBaseInfo,
    listCategory,
    updateBaseForm,
    addCustomer,
    addBaseProduct,
    changeReleaseStatus,
    updateBaseProduct,
    createOtaTask,
    addMock,
    setOnlyFoot,
    updateBaseApplication,
    changeApplicationStatus
  };
});
